import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserModel from "../models/IUserModel";
import ISupplierModel from "../models/ISupplierModel";
import IRMA from "../models/IRMA";
import ICreateRmaDTO from "../models/DTO/ICreateRmaDTO";
import IUpdateRmaDTO from "../models/DTO/IUpdateRmaDTO";
import IReceiveRmaDTO from "../models/DTO/IReceiveRmaDTO";
import IRecordInspectionDTO from "../models/DTO/IRecordInspectionDTO";
import IUpdateInspectionDTO from "../models/DTO/IUpdateInspectionDTO";
import RmaStatusEnum from "@models/RmaStatusEnum";

interface RmaApiResponse<T = any> {
    success: boolean;
    message?: string;
    rma?: IRMA;
    rmas?: IRMA[];
    inspections?: any[];
    summary?: any;
    data?: T;
}

/**
 * Get a single RMA with all associations (line items, inspections, status history)
 */
const getRma = async (rmaGuid: string, userModel: IUserModel, supplierModel: ISupplierModel): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.GetRma,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch RMA: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Update RMA details (notes, tracking number)
 * Only allowed when status is draft
 */
const updateRma = async (
    rmaGuid: string,
    data: Partial<IUpdateRmaDTO>,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.UpdateRma,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error(`Failed to update RMA: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Delete RMA (soft delete)
 * Only allowed when status is draft
 */
const deleteRma = async (rmaGuid: string, userModel: IUserModel, supplierModel: ISupplierModel): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.DeleteRma,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "DELETE",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to delete RMA: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Authorize RMA for customer to ship items back
 * Changes status from draft → authorized
 */
const authorizeRma = async (rmaGuid: string, userModel: IUserModel, supplierModel: ISupplierModel): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.AuthorizeRma,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to authorize RMA: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Receive items (supports multiple partial shipments)
 * First call: authorized → received
 * Subsequent calls: increments qty_received
 */
const receiveRma = async (
    rmaGuid: string,
    data: Partial<IReceiveRmaDTO>,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.ReceiveRma,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error(`Failed to receive RMA items: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Process RMA (final step)
 * Validates all items inspected, restocks inventory, changes status to processed
 */
const processRma = async (rmaGuid: string, userModel: IUserModel, supplierModel: ISupplierModel): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.ProcessRma,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to process RMA: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Cancel RMA at any point before processing
 */
const cancelRma = async (rmaGuid: string, userModel: IUserModel, supplierModel: ISupplierModel): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CancelRma,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to cancel RMA: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Record inspection for a line item
 * Can be called multiple times for partial shipments
 */
const recordInspection = async (
    rmaGuid: string,
    lineItemGuid: string,
    data: Partial<IRecordInspectionDTO>,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.RecordInspection,
        params: { rmaGuid, lineItemGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to record inspection: ${error.message}`);
    }

    return response.json();
};

/**
 * Update an existing inspection
 * Only allowed when status is received
 */
const updateInspection = async (
    rmaGuid: string,
    lineItemGuid: string,
    inspectionGuid: string,
    data: Partial<IUpdateInspectionDTO>,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.UpdateInspection,
        params: { rmaGuid, lineItemGuid, inspectionGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error(`Failed to update inspection: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Delete an inspection
 * Only allowed when status is received
 */
const deleteInspection = async (
    rmaGuid: string,
    lineItemGuid: string,
    inspectionGuid: string,
    userModel: IUserModel,
    supplierModel: ISupplierModel
): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.DeleteInspection,
        params: { rmaGuid, lineItemGuid, inspectionGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "DELETE",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to delete inspection: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Get inspection summary for an RMA
 * Shows aggregated inspection statistics
 */
const getInspectionSummary = async (rmaGuid: string, userModel: IUserModel, supplierModel: ISupplierModel): Promise<RmaApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.GetInspectionSummary,
        params: { rmaGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get inspection summary: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Get the badge color for an RMA status
 *
 * @param statusId - The status ID to get the badge color for
 * @returns The badge color css for the status
 */
const getStatusBadgeColor = (statusId: number): string => {
    switch (statusId) {
        case RmaStatusEnum.DRAFT:
            return "bg-secondary";
        case RmaStatusEnum.AUTHORIZED:
            return "bg-primary";
        case RmaStatusEnum.RECEIVED:
            return "bg-warning";
        case RmaStatusEnum.PROCESSED:
            return "bg-success";
        case RmaStatusEnum.CANCELLED:
            return "bg-danger";
        default:
            return "bg-secondary";
    }
};

const RmaService = {
    getRma,
    updateRma,
    deleteRma,
    authorizeRma,
    receiveRma,
    processRma,
    cancelRma,
    recordInspection,
    updateInspection,
    deleteInspection,
    getInspectionSummary,
    getStatusBadgeColor,
};

export default RmaService;
